import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth, SuporteResultado } from '../lib/auth';

interface Empresa {
  id: number;
  nome: string;
  email: string | null;
  plano: string;
  modo_visualizacao?: 'facilities' | 'oficina' | null;
  registro_consumo_habilitado?: boolean;
  alterar_ordem_assinatura_habilitado?: boolean;
  rotulo_numero_serie?: string;
  orcamento_os_habilitado?: boolean;
  filtro_patrimonio_habilitado?: boolean;
  rastreamento_tecnico_habilitado?: boolean;
  trazer_tecnico_associado_unidade_habilitado?: boolean;
  retirar_tipo_chamado_equipamento_habilitado?: boolean;
  ver_equipamentos_os_habilitado?: boolean;
  ocultar_custo_mao_obra_relatorio_chamados_habilitado?: boolean;
  ocultar_valor_hora_tecnico_relatorio_chamados_habilitado?: boolean;
  modo_financeiro_habilitado?: boolean;
  integracao_pagamentos_habilitado?: boolean;
  qrcode_detalhes_habilitado?: boolean;
  somente_gestor_supervisor_pode_reagendar_habilitado?: boolean;
  lancar_dados_os_concluida_habilitado?: boolean;
  somente_gestor_supervisor_pode_ver_valores_habilitado?: boolean;
  tecnico_sem_acesso_rastreamento_habilitado?: boolean;
  incluir_etapas_processo_os_habilitado?: boolean;
  servicos_habilitado?: boolean;
  dados_equipamento_os_materiais_habilitado?: boolean;
  criacao_os_simplificada_habilitado?: boolean;
  retirar_unidade_local_piso_habilitado?: boolean;
  retirar_tela_pmoc_habilitado?: boolean;
  unidade_local_delete_habilitado?: boolean;
  quantidade_usuarios_permitidos?: number | null;
  status: string;
  db_nome: string | null;
  usuarios: number;
}

type CampoPermissaoEmpresa =
  | 'registro_consumo_habilitado'
  | 'alterar_ordem_assinatura_habilitado'
  | 'orcamento_os_habilitado'
  | 'filtro_patrimonio_habilitado'
  | 'rastreamento_tecnico_habilitado'
  | 'trazer_tecnico_associado_unidade_habilitado'
  | 'retirar_tipo_chamado_equipamento_habilitado'
  | 'ver_equipamentos_os_habilitado'
  | 'ocultar_custo_mao_obra_relatorio_chamados_habilitado'
  | 'ocultar_valor_hora_tecnico_relatorio_chamados_habilitado'
  | 'modo_financeiro_habilitado'
  | 'integracao_pagamentos_habilitado'
  | 'qrcode_detalhes_habilitado'
  | 'somente_gestor_supervisor_pode_reagendar_habilitado'
  | 'lancar_dados_os_concluida_habilitado'
  | 'somente_gestor_supervisor_pode_ver_valores_habilitado'
  | 'tecnico_sem_acesso_rastreamento_habilitado'
  | 'incluir_etapas_processo_os_habilitado'
  | 'servicos_habilitado'
  | 'dados_equipamento_os_materiais_habilitado'
  | 'criacao_os_simplificada_habilitado'
  | 'retirar_unidade_local_piso_habilitado'
  | 'retirar_tela_pmoc_habilitado'
  | 'unidade_local_delete_habilitado';

function situacao(empresa: Empresa): { label: string; cor: string; fundo: string } {
  if (!empresa.db_nome) return { label: 'Pendente', cor: '#b45309', fundo: '#fffbeb' };
  if (empresa.status === 'ativo') return { label: 'Ativa', cor: '#166534', fundo: '#f0fdf4' };
  return { label: 'Desativada', cor: '#b91c1c', fundo: '#fef2f2' };
}

function cardResumo(titulo: string, valor: string | number, detalhe: string) {
  return (
    <div
      className="card"
      style={{
        padding: 18,
        borderRadius: 18,
        border: '1px solid #e5edf7',
        boxShadow: '0 10px 30px rgba(15,23,42,.05)',
      }}
    >
      <div style={{ color: 'var(--texto-suave)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {titulo}
      </div>
      <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{valor}</div>
      <div style={{ marginTop: 6, color: 'var(--texto-suave)', fontSize: 13 }}>{detalhe}</div>
    </div>
  );
}

function normalizarTexto(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function filtraUsuariosAtivos(empresa: Empresa, filtro: string): boolean {
  const total = Number(empresa.usuarios || 0);
  switch (filtro) {
    case 'sem_usuarios':
      return total === 0;
    case 'com_usuarios':
      return total > 0;
    case 'ate_5':
      return total >= 1 && total <= 5;
    case '6_20':
      return total >= 6 && total <= 20;
    case '21_mais':
      return total >= 21;
    default:
      return true;
  }
}

function filtraStatusEmpresa(empresa: Empresa, filtro: string): boolean {
  switch (filtro) {
    case 'ativas':
      return Boolean(empresa.db_nome) && empresa.status === 'ativo';
    case 'inativas':
      return Boolean(empresa.db_nome) && empresa.status !== 'ativo';
    case 'pendentes':
      return !empresa.db_nome;
    default:
      return true;
  }
}

const EMPRESAS_POR_PAGINA = 5;

export default function Empresas() {
  const { acessarSuporte } = useAuth();
  const nav = useNavigate();
  const [lista, setLista] = useState<Empresa[]>([]);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState<number | null>(null);
  const [limitesUsuarios, setLimitesUsuarios] = useState<Record<number, string>>({});
  const [rotulosSerie, setRotulosSerie] = useState<Record<number, string>>({});
  const [emailSuporte, setEmailSuporte] = useState('');
  const [suportePendente, setSuportePendente] = useState<SuporteResultado | null>(null);
  const [suporteCarregando, setSuporteCarregando] = useState(false);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroUsuarios, setFiltroUsuarios] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todas');
  const [empresasExpandidas, setEmpresasExpandidas] = useState<Record<number, boolean>>({});
  const [paginaAtual, setPaginaAtual] = useState(1);

  async function carregar() {
    setErro('');
    try {
      const dados = await api<Empresa[]>('/empresas/empresas.php');
      setLista(dados);
      setLimitesUsuarios(
        Object.fromEntries(
          dados.map((empresa) => [empresa.id, empresa.quantidade_usuarios_permitidos ? String(empresa.quantidade_usuarios_permitidos) : '']),
        ),
      );
      setEmpresasExpandidas((atual) =>
        Object.fromEntries(
          dados.map((empresa) => [empresa.id, atual[empresa.id] ?? false]),
        ),
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  async function acao(id: number, body: Record<string, unknown>, sucesso: string) {
    setMsg('');
    setErro('');
    setOcupado(id);
    try {
      await api('/empresas/empresas.php', { method: 'PUT', body: { id, ...body } });
      setMsg(sucesso);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  async function iniciarSuporte(empresaId?: number) {
    const email = emailSuporte.trim();
    setMsg('');
    setErro('');
    setSuporteCarregando(true);
    try {
      const resultado = await acessarSuporte(email, empresaId);
      if (resultado.precisaEscolher) {
        setSuportePendente(resultado);
        setMsg('Escolha a empresa vinculada a esse cliente.');
        return;
      }
      setSuportePendente(null);
      nav('/app');
      window.location.reload();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSuporteCarregando(false);
    }
  }

  async function salvarLimiteUsuarios(empresa: Empresa) {
    const bruto = (limitesUsuarios[empresa.id] || '').trim();
    if (bruto !== '' && (!/^\d+$/.test(bruto) || Number(bruto) < 1)) {
      setErro('Informe uma quantidade de usuários permitidos maior que zero.');
      return;
    }

    await acao(
      empresa.id,
      { quantidade_usuarios_permitidos: bruto === '' || bruto === '0' ? null : Number(bruto) },
      bruto === '' || bruto === '0' ? 'Limite de usuários removido.' : 'Quantidade de usuários permitidos atualizada.',
    );
  }

  async function salvarRotuloSerie(empresa: Empresa) {
    const bruto = (rotulosSerie[empresa.id] ?? empresa.rotulo_numero_serie ?? '').trim();
    await acao(
      empresa.id,
      { rotulo_numero_serie: bruto || 'Número de série' },
      'Nome do campo atualizado.',
    );
  }

  function alternarPermissaoEmpresa(
    empresa: Empresa,
    campo: CampoPermissaoEmpresa,
    habilitado: boolean,
    mensagemAoLigar: string,
    mensagemAoDesligar: string,
  ) {
    return acao(empresa.id, { [campo]: habilitado }, habilitado ? mensagemAoLigar : mensagemAoDesligar);
  }

  const listaOrdenada = [...lista].sort((a, b) => {
    const pesoA = a.db_nome ? 1 : 0;
    const pesoB = b.db_nome ? 1 : 0;
    if (pesoA !== pesoB) return pesoA - pesoB;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
  const termoEmpresa = normalizarTexto(filtroEmpresa);
  const listaFiltrada = listaOrdenada.filter((empresa) => {
    const nomeOk = !termoEmpresa || normalizarTexto(empresa.nome).includes(termoEmpresa);
    return nomeOk && filtraUsuariosAtivos(empresa, filtroUsuarios) && filtraStatusEmpresa(empresa, filtroStatus);
  });
  const totalPaginas = Math.max(1, Math.ceil(listaFiltrada.length / EMPRESAS_POR_PAGINA));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const inicioPagina = (paginaSegura - 1) * EMPRESAS_POR_PAGINA;
  const listaPaginada = listaFiltrada.slice(inicioPagina, inicioPagina + EMPRESAS_POR_PAGINA);

  const pendentes = lista.filter((empresa) => !empresa.db_nome).length;
  const ativas = lista.filter((empresa) => empresa.db_nome && empresa.status === 'ativo').length;
  const usuariosAtivos = lista.reduce((acc, empresa) => acc + Number(empresa.usuarios || 0), 0);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroEmpresa, filtroUsuarios, filtroStatus]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  function alternarExpansao(empresaId: number) {
    setEmpresasExpandidas((atual) => ({
      ...atual,
      [empresaId]: !atual[empresaId],
    }));
  }

  function definirExpansaoTodas(expandido: boolean) {
    setEmpresasExpandidas((atual) => ({
      ...atual,
      ...Object.fromEntries(listaFiltrada.map((empresa) => [empresa.id, expandido])),
    }));
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Empresas</h1>
        <button className="btn sec sm" onClick={carregar}>
          Atualizar
        </button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 14,
          marginBottom: 16,
        }}
      >
        {cardResumo('Total de empresas', lista.length, 'Visão geral da base cadastrada')}
        {cardResumo('Pendentes', pendentes, pendentes ? 'Aguardando aprovação para criar ambiente' : 'Nenhuma empresa aguardando aprovação')}
        {cardResumo('Ativas', ativas, 'Empresas com ambiente pronto e status ativo')}
        {cardResumo('Usuários ativos', usuariosAtivos, 'Soma de usuários ativos em todas as empresas')}
      </div>

      <div
        className="card"
        style={{
          marginBottom: 16,
          borderRadius: 22,
          border: '1px solid #dce8f8',
          background: 'linear-gradient(135deg, #f8fbff 0%, #ffffff 72%)',
          boxShadow: '0 16px 40px rgba(15,23,42,.06)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 560 }}>
            <h2 style={{ margin: 0, fontSize: 22 }}>Acesso de suporte</h2>
            <p style={{ margin: '8px 0 0', color: 'var(--texto-suave)', lineHeight: 1.6 }}>
              Entre temporariamente na empresa de um cliente pelo e-mail dele. Se o usuário estiver vinculado a mais de uma empresa,
              o sistema vai pedir a escolha da empresa correta.
            </p>
          </div>
          {pendentes > 0 && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: '#fffbeb',
                color: '#b45309',
                fontWeight: 700,
                border: '1px solid #fde68a',
              }}
            >
              {pendentes} empresa(s) aguardando aprovação
            </div>
          )}
        </div>

        <div
          className="form-grid"
          style={{
            alignItems: 'end',
            gap: 14,
            marginTop: 18,
            gridTemplateColumns: 'minmax(260px, 1.4fr) auto',
          }}
        >
          <label>
            E-mail do cliente
            <input
              type="email"
              value={emailSuporte}
              onChange={(e) => {
                setEmailSuporte(e.target.value);
                setSuportePendente(null);
              }}
              placeholder="cliente@empresa.com"
            />
          </label>
          <button className="btn" disabled={suporteCarregando || !emailSuporte.trim()} onClick={() => iniciarSuporte()}>
            {suporteCarregando ? 'Entrando...' : 'Entrar para suporte'}
          </button>
        </div>

        {suportePendente?.empresas?.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            {suportePendente.empresas.map((empresa) => (
              <button
                key={empresa.empresa_id}
                className="btn sec sm"
                disabled={suporteCarregando}
                onClick={() => iniciarSuporte(empresa.empresa_id)}
              >
                {empresa.empresa_nome}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 18,
          borderRadius: 20,
          border: '1px solid #dbe6f5',
          display: 'grid',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Busca rápida</div>
            <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 14 }}>
              Filtre empresas e recolha os cards para navegar mais rápido.
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button className="btn sec sm" onClick={() => definirExpansaoTodas(true)} disabled={!listaFiltrada.length}>
              Expandir todas
            </button>
            <button className="btn sec sm" onClick={() => definirExpansaoTodas(false)} disabled={!listaFiltrada.length}>
              Recolher todas
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1.2fr) minmax(220px, .8fr) minmax(220px, .8fr)',
            gap: 12,
          }}
        >
          <label>
            Filtrar por empresa
            <input
              className="input"
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
              placeholder="Digite o nome da empresa"
            />
          </label>
          <label>
            Filtrar por usuários ativos
            <select value={filtroUsuarios} onChange={(e) => setFiltroUsuarios(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="sem_usuarios">Sem usuários ativos</option>
              <option value="com_usuarios">Com usuários ativos</option>
              <option value="ate_5">De 1 a 5 usuários</option>
              <option value="6_20">De 6 a 20 usuários</option>
              <option value="21_mais">21 ou mais usuários</option>
            </select>
          </label>
          <label>
            Filtrar por status
            <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
              <option value="todas">Todas</option>
              <option value="ativas">Ativas</option>
              <option value="inativas">Inativas</option>
              <option value="pendentes">Pendentes</option>
            </select>
          </label>
        </div>

        <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
          Exibindo {listaPaginada.length} de {listaFiltrada.length} empresa(s) filtradas. Total geral: {lista.length}.
        </div>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {listaPaginada.map((empresa) => {
          const status = situacao(empresa);
          const carregandoEsta = ocupado === empresa.id;
          const expandida = Boolean(empresasExpandidas[empresa.id]);

          const renderPermissao = (
            campo: CampoPermissaoEmpresa,
            titulo: string,
            descricao: string,
            mensagemAoLigar: string,
            mensagemAoDesligar: string,
          ) => (
            <label
              className="check-line"
              style={{
                alignItems: 'flex-start',
                gap: 12,
                padding: 14,
                borderRadius: 16,
                border: '1px solid #e6edf7',
                background: '#fbfdff',
                minHeight: 88,
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(empresa[campo])}
                disabled={carregandoEsta}
                onChange={(e) =>
                  alternarPermissaoEmpresa(empresa, campo, e.target.checked, mensagemAoLigar, mensagemAoDesligar).catch((err) =>
                    setErro(err instanceof Error ? err.message : String(err)),
                  )
                }
                style={{ marginTop: 2 }}
              />
              <span>
                <strong style={{ display: 'block', color: '#0f172a' }}>{titulo}</strong>
                <span style={{ display: 'block', marginTop: 4, color: 'var(--texto-suave)', lineHeight: 1.45 }}>{descricao}</span>
              </span>
            </label>
          );

          return (
            <section
              key={empresa.id}
              className="card"
              style={{
                borderRadius: 24,
                border: '1px solid #dbe6f5',
                boxShadow: '0 18px 46px rgba(15,23,42,.06)',
                padding: 22,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{empresa.nome}</div>
                  <div style={{ marginTop: 6, color: 'var(--texto-suave)', fontSize: 14 }}>{empresa.email || 'Sem e-mail cadastrado'}</div>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 10, color: 'var(--texto-suave)', fontSize: 13 }}>
                    <span>Plano: <strong style={{ color: '#0f172a' }}>{empresa.plano}</strong></span>
                    <span>Usuários ativos: <strong style={{ color: '#0f172a' }}>{empresa.usuarios}</strong></span>
                    <span>Visualização: <strong style={{ color: '#0f172a' }}>{empresa.modo_visualizacao === 'oficina' ? 'Oficina' : 'Padrão'}</strong></span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <span
                    className="badge"
                    style={{
                      background: status.fundo,
                      color: status.cor,
                      border: `1px solid ${status.cor}33`,
                    }}
                  >
                    {status.label}
                  </span>
                  <span className="badge" style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe' }}>
                    {empresa.usuarios} usuário(s) ativos
                  </span>
                  <span className="badge" style={{ background: empresa.db_nome ? '#eff6ff' : '#fff7ed', color: empresa.db_nome ? '#1d4ed8' : '#c2410c' }}>
                    {empresa.db_nome ? 'Ambiente criado' : 'Aguardando ambiente'}
                  </span>
                  <button className="btn sec sm" onClick={() => alternarExpansao(empresa.id)}>
                    {expandida ? 'Recolher' : 'Expandir'}
                  </button>
                </div>
              </div>

              {expandida && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 14,
                    marginTop: 18,
                  }}
                >
                  <label>
                    Plano
                    <select
                      value={empresa.plano}
                      disabled={carregandoEsta}
                      onChange={(e) => acao(empresa.id, { plano: e.target.value }, 'Plano atualizado.')}
                    >
                      <option value="simples">Simples</option>
                      <option value="premium">Premium</option>
                    </select>
                  </label>

                  <label>
                    Visualização
                    <select
                      value={empresa.modo_visualizacao || 'facilities'}
                      disabled={carregandoEsta}
                      onChange={(e) => acao(empresa.id, { modo_visualizacao: e.target.value }, 'Modo de visualização atualizado.')}
                    >
                      <option value="facilities">Padrão</option>
                      <option value="oficina">Oficina</option>
                    </select>
                  </label>

                  <div
                    style={{
                      border: '1px solid #e6edf7',
                      borderRadius: 16,
                      padding: '14px 16px',
                      background: '#fbfdff',
                    }}
                  >
                    <div style={{ color: 'var(--texto-suave)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      Uso atual
                    </div>
                    <div style={{ marginTop: 8, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{empresa.usuarios}</div>
                    <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 13 }}>Usuários ativos nesta empresa</div>
                  </div>

                  <div
                    style={{
                      border: '1px solid #e6edf7',
                      borderRadius: 16,
                      padding: 14,
                      background: '#fbfdff',
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <label>
                      Limite de usuários
                      <input
                        className="input"
                        inputMode="numeric"
                        placeholder="Sem limite"
                        value={limitesUsuarios[empresa.id] ?? ''}
                        disabled={carregandoEsta}
                        onChange={(e) =>
                          setLimitesUsuarios((atual) => ({
                            ...atual,
                            [empresa.id]: e.target.value.replace(/[^\d]/g, ''),
                          }))
                        }
                      />
                    </label>
                    <button
                      className="btn sec sm"
                      disabled={carregandoEsta}
                      onClick={() => salvarLimiteUsuarios(empresa).catch((err) => setErro(err instanceof Error ? err.message : String(err)))}
                    >
                      Salvar limite
                    </button>
                  </div>

                  <div
                    style={{
                      border: '1px solid #e6edf7',
                      borderRadius: 16,
                      padding: 14,
                      background: '#fbfdff',
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <label>
                      Nome do campo "Número de série" (tela de Equipamentos)
                      <input
                        className="input"
                        placeholder="Número de série"
                        value={rotulosSerie[empresa.id] ?? empresa.rotulo_numero_serie ?? ''}
                        disabled={carregandoEsta}
                        maxLength={60}
                        onChange={(e) =>
                          setRotulosSerie((atual) => ({
                            ...atual,
                            [empresa.id]: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <button
                      className="btn sec sm"
                      disabled={carregandoEsta}
                      onClick={() => salvarRotuloSerie(empresa).catch((err) => setErro(err instanceof Error ? err.message : String(err)))}
                    >
                      Salvar nome do campo
                    </button>
                  </div>
                </div>
              )}

              {expandida && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>Permissões da empresa</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {renderPermissao(
                      'registro_consumo_habilitado',
                      'Registro de consumo',
                      'Libera a visualização do módulo Registro de Consumo para a empresa.',
                      'Permissão de Registro de Consumo habilitada.',
                      'Permissão de Registro de Consumo desabilitada.',
                    )}
                    {renderPermissao(
                      'alterar_ordem_assinatura_habilitado',
                      'Alterar a ordem da assinatura',
                      'Move a coleta de assinatura para dentro da etapa de finalização e força a sequência Checklist, Materiais, Observação e Finalização.',
                      'Permissão "Alterar a ordem da assinatura" habilitada.',
                      'Permissão "Alterar a ordem da assinatura" desabilitada.',
                    )}
                    {renderPermissao(
                      'orcamento_os_habilitado',
                      'Orçamento na OS',
                      'Permite trabalhar com orçamento dentro das ordens de serviço.',
                      'Permissão de Orçamento da OS habilitada.',
                      'Permissão de Orçamento da OS desabilitada.',
                    )}
                    {renderPermissao(
                      'filtro_patrimonio_habilitado',
                      'Filtro por patrimônio',
                      'Exibe o filtro de patrimônio nas telas que usam esse agrupamento.',
                      'Permissão de filtro por patrimônio habilitada.',
                      'Permissão de filtro por patrimônio desabilitada.',
                    )}
                    {renderPermissao(
                      'rastreamento_tecnico_habilitado',
                      'Rastreamento do técnico',
                      'Habilita recursos de mapa e localização para técnicos dessa empresa.',
                      'Rastreamento de técnico habilitado para esta empresa.',
                      'Rastreamento de técnico desabilitado para esta empresa.',
                    )}
                    {renderPermissao(
                      'trazer_tecnico_associado_unidade_habilitado',
                      'Trazer técnico associado à unidade',
                      'Ao criar chamado, o campo Responsável mostra apenas técnicos vinculados à unidade selecionada.',
                      'Permissão "Trazer técnico associado à unidade" habilitada.',
                      'Permissão "Trazer técnico associado à unidade" desabilitada.',
                    )}
                    {renderPermissao(
                      'retirar_tipo_chamado_equipamento_habilitado',
                      'Retirar tipo de chamado para equipamento',
                      'No cadastro e na edição de ativos, o Tipo de Chamado deixa de ser obrigatório e pode ficar oculto.',
                      'Permissão "Retirar tipo de chamado para equipamento" habilitada.',
                      'Permissão "Retirar tipo de chamado para equipamento" desabilitada.',
                    )}
                    {renderPermissao(
                      'ver_equipamentos_os_habilitado',
                      'VER EQUIPAMENTOS NAS ORDENS DE SERVIÇO',
                      'Permite associar clientes aos equipamentos (tela Equipamentos), ver os equipamentos do cliente ao abrir a OS e compartilhar o Portal do Cliente por Relatórios.',
                      'Permissão "Ver equipamentos nas ordens de serviço" habilitada.',
                      'Permissão "Ver equipamentos nas ordens de serviço" desabilitada.',
                    )}
                    {renderPermissao(
                      'ocultar_custo_mao_obra_relatorio_chamados_habilitado',
                      'N\u00C3O VER CUSTO M\u00C3O DE OBRA',
                      'Quando ligada, oculta o custo de m\u00E3o de obra no relat\u00F3rio "Relat\u00F3rio de chamados (completo)".',
                      'Permiss\u00E3o "N\u00E3o ver custo m\u00E3o de obra" habilitada.',
                      'Permiss\u00E3o "N\u00E3o ver custo m\u00E3o de obra" desabilitada.',
                    )}
                    {renderPermissao(
                      'ocultar_valor_hora_tecnico_relatorio_chamados_habilitado',
                      'N\u00C3O VER VALOR HORA T\u00C9CNICO',
                      'Quando ligada, oculta o valor/hora do t\u00E9cnico no relat\u00F3rio "Relat\u00F3rio de chamados (completo)".',
                      'Permiss\u00E3o "N\u00E3o ver valor hora t\u00E9cnico" habilitada.',
                      'Permiss\u00E3o "N\u00E3o ver valor hora t\u00E9cnico" desabilitada.',
                    )}
                    {renderPermissao(
                      'modo_financeiro_habilitado',
                      'ATIVAR MODO FINANCEIRO',
                      'Marca esta empresa como habilitada para o modo financeiro. Por enquanto apenas registra a libera\u00E7\u00E3o: nenhuma tela ou fluxo muda ao ligar.',
                      'Permiss\u00E3o "Ativar modo financeiro" habilitada.',
                      'Permiss\u00E3o "Ativar modo financeiro" desabilitada.',
                    )}
                    {renderPermissao(
                      'integracao_pagamentos_habilitado',
                      'ATIVAR INTEGRA\u00C7\u00C3O PAGAMENTOS',
                      'Marca esta empresa como habilitada para a integra\u00E7\u00E3o de pagamentos. Por enquanto apenas registra a libera\u00E7\u00E3o: nenhuma tela ou fluxo muda ao ligar.',
                      'Permiss\u00E3o "Ativar integra\u00E7\u00E3o pagamentos" habilitada.',
                      'Permiss\u00E3o "Ativar integra\u00E7\u00E3o pagamentos" desabilitada.',
                    )}
                    {renderPermissao(
                      'somente_gestor_supervisor_pode_reagendar_habilitado',
                      'SÓ PODE REAGENDAR QUEM É GESTOR OU SUPERVISOR',
                      'Mantém o reagendamento e a troca de técnico restritos aos perfis de gestor da empresa e supervisor.',
                      'Permissão "Só pode reagendar quem é gestor ou supervisor" habilitada.',
                      'Permissão "Só pode reagendar quem é gestor ou supervisor" desabilitada.',
                    )}
                    {renderPermissao(
                      'lancar_dados_os_concluida_habilitado',
                      'LANÇAR DADOS EM ORDENS DE SERVIÇO JÁ CONCLUÍDA',
                      'Destrava a OS concluída para receber novos dados (evidências, checklist, materiais, serviços e assinaturas), mas somente para gestor da empresa e supervisor. Técnico e solicitante continuam bloqueados, e OS cancelada segue travada para todos.',
                      'Permissão "Lançar dados em ordens de serviço já concluída" habilitada.',
                      'Permissão "Lançar dados em ordens de serviço já concluída" desabilitada.',
                    )}
                    {renderPermissao(
                      'somente_gestor_supervisor_pode_ver_valores_habilitado',
                      'SOMENTE SUPERVISOR OU GESTOR PODE VER VALORES',
                      'Oculta valores monetários da OS para técnicos, como orçamento, valor/hora, mão de obra e preços de materiais - e some com todo valor do documento/PDF enviado ao cliente. Continua aparecendo no relatório emitido em Relatórios (gestor/supervisor).',
                      'Permissão "Somente supervisor ou gestor pode ver valores" habilitada.',
                      'Permissão "Somente supervisor ou gestor pode ver valores" desabilitada.',
                    )}
                    {renderPermissao(
                      'tecnico_sem_acesso_rastreamento_habilitado',
                      'TÉCNICO NÃO TERÁ ACESSO À TELA DE RASTREAMENTO',
                      'Bloqueia o mapa e a tela de rastreamento para usuários com perfil técnico, mesmo quando a empresa usa rastreamento.',
                      'Permissão "Técnico não terá acesso à tela de rastreamento" habilitada.',
                      'Permissão "Técnico não terá acesso à tela de rastreamento" desabilitada.',
                    )}
                    {renderPermissao(
                      'qrcode_detalhes_habilitado',
                      'QRCODE DETALHES',
                      'Libera a visualiza\u00E7\u00E3o da ficha completa e do hist\u00F3rico quando o QR Code de um local ou equipamento for escaneado.',
                      'Permiss\u00E3o "QRCODE DETALHES" habilitada.',
                      'Permiss\u00E3o "QRCODE DETALHES" desabilitada.',
                    )}
                    {renderPermissao(
                      'incluir_etapas_processo_os_habilitado',
                      'INCLUIR ETAPAS NO PROCESSO DE OS',
                      'Na EXECUÇÃO da OS, cada técnico agendado recebe as 12 etapas do processo, com imagem e checklist próprios e em sequência. O técnico não conclui a OS: quem fecha é o gestor. A criação da OS não muda.',
                      'Permiss\u00E3o "Incluir etapas no processo de OS" habilitada.',
                      'Permiss\u00E3o "Incluir etapas no processo de OS" desabilitada.',
                    )}
                    {renderPermissao(
                      'servicos_habilitado',
                      'SERVI\u00C7OS',
                      'Libera o menu lateral "Servi\u00E7os" s\u00F3 para esta empresa: tabela de pre\u00E7os de servi\u00E7o (preventiva x corretiva por faixa de capacidade, manuten\u00E7\u00E3o por BTU, instala\u00E7\u00E3o, gases e bebedouros) e a importa\u00E7\u00E3o da planilha do cliente. Desligada, o menu desaparece e os dados ficam inacess\u00EDveis.',
                      'Permiss\u00E3o "Servi\u00E7os" habilitada.',
                      'Permiss\u00E3o "Servi\u00E7os" desabilitada.',
                    )}
                    {renderPermissao(
                      'dados_equipamento_os_materiais_habilitado',
                      'PERMITIR DADOS EQUIPAMENTOS NO DETALHES E EM MATERIAIS',
                      'Mostra Capacidade e CA (patrim\u00F4nio) do equipamento no detalhe da OS e na aba Materiais. Antes esse bloco existia s\u00F3 para uma empresa, fixado no c\u00F3digo pelo nome/e-mail; agora \u00E9 permiss\u00E3o e vale para qualquer empresa.',
                      'Permiss\u00E3o "Permitir dados equipamentos no detalhe e em materiais" habilitada.',
                      'Permiss\u00E3o "Permitir dados equipamentos no detalhe e em materiais" desabilitada.',
                    )}
                    {renderPermissao(
                      'criacao_os_simplificada_habilitado',
                      'CRIA\u00C7\u00C3O DE OS SIMPLIFICADA - GERADA PARA O CLIENTE CLIMATIZE "REFRIGERA\u00C7\u00C3O"',
                      'Liga os quatro fluxos curtos que este cliente pediu, na ordem dele. (1) NOVA OS: Cliente \u2192 Servi\u00E7os \u2192 T\u00E9cnico \u2192 Revis\u00E3o \u2192 Imprimir, com servi\u00E7o lan\u00E7ado por descri\u00E7\u00E3o/quantidade/valor somando o total da OS. (2) CADASTRO DO CLIENTE numa \u00FAnica tela: Nome \u2192 Endere\u00E7o \u2192 Documentos \u2192 Equipamentos \u2192 Ambientes. (3) NOVO PMOC puxando ambientes e equipamentos do cliente, ajustando s\u00F3 cronograma, documento t\u00E9cnico e MTTR/MTBF. (4) Equipamento com BTU e ambiente lan\u00E7ados em sequ\u00EAncia. Funciona SOZINHA: n\u00E3o exige a permiss\u00E3o "Servi\u00E7os" nem outro m\u00F3dulo. ATEN\u00C7\u00C3O: o cadastro pelo cliente cria automaticamente uma Unidade (nome do cliente), um Piso "\u00danico" e um Local por ambiente \u2014 eles aparecem nas telas de Unidades e Locais. O formul\u00E1rio completo de OS e a tela de PMOC continuam no ar.',
                      'Permiss\u00E3o "Cria\u00E7\u00E3o de OS simplificada" habilitada.',
                      'Permiss\u00E3o "Cria\u00E7\u00E3o de OS simplificada" desabilitada.',
                    )}
                    {renderPermissao(
                      'retirar_unidade_local_piso_habilitado',
                      'RETIRAR UNIDADE - LOCAL - PISO',
                      'Some com a hierarquia Unidade > Piso > Local em todo o sistema para esta empresa: as abas Unidades, Pisos e Locais saem de Cadastros, o passo Local sai da Nova OS, o bloco Localizacao sai do Equipamento, e as colunas/filtros de unidade saem das listas, do detalhe da OS, dos relatorios e do PMOC. NAO barra nada: criar OS, executar OS e criar equipamento continuam funcionando, com esses campos vazios. Os dados ja gravados nao sao apagados - desligando a permissao, tudo reaparece.',
                      'Permissao "Retirar unidade - local - piso" habilitada.',
                      'Permissao "Retirar unidade - local - piso" desabilitada.',
                    )}
                    {renderPermissao(
                      'retirar_tela_pmoc_habilitado',
                      'RETIRAR TELA PMOC',
                      'Some com TUDO relacionado a PMOC para esta empresa: o menu lateral PMOC, as rotas, a aba PMOC de Cadastros, os passos Cronograma / MTTR-MTBF / Gerar e imprimir na ficha do cliente, o atalho PMOC no equipamento, a coluna Plano PMOC e o relatorio de PMOC. A API tambem e bloqueada, nao so a tela - abrir a URL na mao nao passa. Nao apaga nada: planos, cronogramas e programacoes ficam no banco e reaparecem se a permissao for desligada.',
                      'Permissao "Retirar tela PMOC" habilitada.',
                      'Permissao "Retirar tela PMOC" desabilitada.',
                    )}
                    {renderPermissao(
                      'unidade_local_delete_habilitado',
                      'EXCLUIR UNIDADE/PISO/LOCAL SEM HISTORICO',
                      'Na aba Unidades de Cadastros, cada unidade ganha um resumo expansivel com os pisos e locais dela, e um botao "Excluir" (alem do Ativar/Desativar de sempre) para unidade, piso e local. So funciona quando o registro nao tem nenhuma ordem de servico, equipamento nem sub-nivel apontando pra ele - o backend recusa com aviso e sugere Desativar quando ha uso. Nao muda nada mais na tela.',
                      'Permissao "Excluir unidade/piso/local sem historico" habilitada.',
                      'Permissao "Excluir unidade/piso/local sem historico" desabilitada.',
                    )}
                  </div>
                </div>
              )}

              {expandida && (
                <div
                  className="form-actions"
                  style={{ marginTop: 18 }}
                >
                  {!empresa.db_nome && (
                    <button
                      className="btn sm"
                      disabled={carregandoEsta}
                      onClick={() => acao(empresa.id, { status: 'ativo' }, 'Empresa aprovada! O ambiente foi criado.')}
                    >
                      {carregandoEsta ? 'Aprovando...' : 'Aprovar'}
                    </button>
                  )}
                  {empresa.db_nome && empresa.status === 'ativo' && (
                    <button
                      className="btn sec sm"
                      disabled={carregandoEsta}
                      onClick={() => acao(empresa.id, { status: 'inativo' }, 'Empresa desativada.')}
                    >
                      Desativar
                    </button>
                  )}
                  {empresa.db_nome && empresa.status !== 'ativo' && (
                    <button
                      className="btn sm"
                      disabled={carregandoEsta}
                      onClick={() => acao(empresa.id, { status: 'ativo' }, 'Empresa reativada.')}
                    >
                      Reativar
                    </button>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {!listaFiltrada.length && (
          <div className="card" style={{ textAlign: 'center', padding: 28 }}>
            Nenhuma empresa encontrada com esse filtro.
          </div>
        )}

        {listaFiltrada.length > 0 && (
          <div
            className="card"
            style={{
              padding: 16,
              borderRadius: 18,
              border: '1px solid #dbe6f5',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ color: 'var(--texto-suave)', fontSize: 14 }}>
              Página {paginaSegura} de {totalPaginas}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual(1)}>
                Primeira
              </button>
              <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual((atual) => Math.max(1, atual - 1))}>
                Anterior
              </button>
              <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual((atual) => Math.min(totalPaginas, atual + 1))}>
                Próxima
              </button>
              <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual(totalPaginas)}>
                Última
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
